import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  CaasComponent,
  CaasComponents,
  CaasLayout,
  TranslatedComponents,
} from '../../../shared/interfaces/caas/caas.interface';
import { CaasMainSection } from '../../../shared/interfaces/caas/caas.interface';
import { AccordionItem } from '../../../shared/interfaces/accordion-item.interface';
import { UtilitiesCards } from '../../../shared/interfaces/utilities-cards.interface';
import { StaticGraphicComponent } from '../../molecules/static-graphic/static-graphic.component';
import { HighlightcardListComponent } from '../../molecules/highlightcard-list/highlightcard-list.component';
import { TextBlockComponent } from '../../atoms/text-block/text-block.component';
import { UtilitiesCardsComponent } from '../../molecules/utilities-cards/utilities-cards.component';
import { PodcastComponent } from '../accordion/podcast/podcast.component';
import { MaterialDownloadComponent } from '../../molecules/material-download/material-download.component';
import { LogoWallComponent } from '../../molecules/logo-wall/logo-wall.component';
import { CrossReferencingComponent } from '../cross-referencing/cross-referencing.component';
import { PrefooterComponent } from '../prefooter/prefooter.component';
import { CommonModule } from '@angular/common';
import { TeaserContainerComponent } from '../teaser-container/teaser-container.component';
import { SliderComponent } from '../slider/slider.component';
import { ContactModuleComponent } from '../../molecules/contact-module/contact-module.component';
import { InfoTeasersComponent } from '../../molecules/info-teasers/info-teasers.component';
import { TermineComponent } from '../accordion/termine/termine.component';
import { QuoteComponent } from '../../molecules/quote/quote.component';
import { AccordionComponent } from '../accordion/accordion.component';
import { HelpDisruptorComponent } from '../help-disruptor/help-disruptor.component';
import { ManipulateDataService } from '../../../services/translate.service';
import { Headlines } from '../../../shared/enums/shared.enums';
import {
  PodcastItems,
  PodcastPlatforms,
} from '../../../shared/interfaces/podcast-item.interface';
import { News, NewsFilter } from '../../../shared/interfaces/news.interface';
import { PodcastService } from '../../../services/podcast.service';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { Router } from '@angular/router';
import { ImagesService } from '../../../services/images.service';
import { ImageMetaData } from '../../../shared/interfaces/meta-data.interface';
import { TagsService } from '../../../services/tags.service';

@Component({
  selector: 'hdi-section',
  standalone: true,
  templateUrl: './section.component.html',
  styleUrls: ['./section.component.scss'],
  imports: [
    CommonModule,
    StaticGraphicComponent,
    HighlightcardListComponent,
    TextBlockComponent,
    UtilitiesCardsComponent,
    PodcastComponent,
    MaterialDownloadComponent,
    LogoWallComponent,
    CrossReferencingComponent,
    PrefooterComponent,
    TeaserContainerComponent,
    SliderComponent,
    ContactModuleComponent,
    InfoTeasersComponent,
    TermineComponent,
    QuoteComponent,
    AccordionComponent,
    HelpDisruptorComponent,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class SectionComponent implements OnChanges, AfterViewInit {
  @Input() backgroundColor: 'white' | 'grey' = 'white';
  @Input() id: string = '';
  @Input() paddingTop?: boolean = false;
  @Input() paddingBottom?: boolean = false;
  @Input() paddingBlock?: boolean = true;
  @Input() mainSection?: CaasMainSection;
  @Input() podcastData: {
    podcasts: PodcastItems[];
    platforms: PodcastPlatforms[];
  } = { podcasts: [], platforms: [] };
  @Input() news: News[] = [];
  @Input() newsFilters: NewsFilter[] = [];
  @Input() tags: { name: string; identifier: string }[] = [];
  @Input() eventsData: CaasLayout[] = [];
  @Input() imagesMetaData: ImageMetaData[] = [];

  public accordionItems: AccordionItem[] = [];
  public utilitiesCardsList: UtilitiesCards[] = [];
  public materialDownloadInclude: boolean = false;
  public materialDownloadDeepLinks: CaasComponents['value'] | undefined;
  public translatedComponents: TranslatedComponents = {
    news: [],
    newsFilters: [],
    texts: [],
    images: [],
    highlights: [],
    termines: [],
    helpdisruptors: [],
    teasers: [],
    prefooters: [],
    downloads: [],
    deeplinks: [],
    trustedAwards: [],
    contacts: [],
    podcasts: [],
    podcastPlatforms: [],
    explanations: [],
    linktiles: [],
    events: [],
    accordeons: [],
    quotes: [],
    sliderQuotes: [],
  };
  orderedComponents: CaasComponent[] = [];
  Headlines = Headlines;

  constructor(
    private manipulateDataService: ManipulateDataService,
    private cdRef: ChangeDetectorRef,
    private podcastService: PodcastService,
    private router: Router,
    private caasRoutesService: CaasRoutesService,
    private imagesService: ImagesService,
    private tagsService: TagsService
  ) {}

  ngAfterViewInit(): void {
    if (this.news?.length) {
      this.manipulateDataService.setNews(this.news);
    }
    if (this.newsFilters?.length) {
      this.manipulateDataService.setNewsFilters(this.newsFilters);
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    // Handle podcast data changes
    if (changes['podcastData'] && this.podcastData) {
      this.podcastService.setPodcasts(this.podcastData.podcasts);
      this.podcastService.setPlatforms(this.podcastData.platforms);
    }

    if (changes['tags']) {
      this.tagsService.setTags(changes['tags'].currentValue);
    }

    // Handle images metadata changes
    if (changes['imagesMetaData']) {
      this.imagesService.setImagesMetaData(
        changes['imagesMetaData'].currentValue
      );
    }

    // Handle mainSection changes
    if (changes['mainSection'] && this.mainSection) {
      try {
        this.translatedComponents =
          await this.manipulateDataService.translateComponents(
            this.mainSection
          );

        // Set ordered components after translation
        this.orderedComponents =
          this.mainSection?.formData?.st_items?.value || [];
        // console.log('Sections:', this.orderedComponents);
        // console.log('Translated Components:', this.translatedComponents);
        this.accordionItems = this.manipulateDataService.getAccordionItems(
          this.mainSection
        );
        this.utilitiesCardsList =
          this.manipulateDataService.getUtilitiesCardsList(this.mainSection);
        this.materialDownloadInclude =
          this.manipulateDataService.checkMaterialDownloadInclude(
            this.mainSection
          );

        if (this.materialDownloadInclude) {
          this.materialDownloadDeepLinks =
            this.manipulateDataService.getMaterialDownloadDeepLinks(
              this.mainSection
            );
        }
        // Set route links for navigation
        this.caasRoutesService.setRouteLinks(
          this.router.config
            .filter((route) => route.path)
            .map((route) => ({
              identifier: route.data?.['pageIdentifier'],
              path: route.path || '',
            }))
        );

        this.cdRef.detectChanges();
      } catch (error) {
        console.error('Error during mainSection processing:', error);
      }
    }
  }
}
